'use client'

import { useRef } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { dir } from 'i18next'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery, Box, Container, Grid, Typography } from '@mui/material'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// Types
import type { TemplateProps } from '@/types/components/templates'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Fonts
import NexaFont from '@/styles/fonts/nexa'

// Register GSAP plugin
gsap.registerPlugin(useGSAP, ScrollTrigger)

// Components
const CircleMolecule = dynamic(() => import('@/components/molecules/circle'))
const DialogueMolecule = dynamic(() => import('@/components/molecules/dialogue'))
const SectionHeaderMolecule = dynamic(() => import('@/components/molecules/section-header'))
const AwardsMolecule = dynamic(() => import('@/components/molecules/awards'))
const CustomersMolecule = dynamic(() => import('@/components/molecules/customers'))
const CardMoelcule = dynamic(() => import('@/components/molecules/items/card'))
const CircleShapeAtom = dynamic(() => import('@/components/atoms/shapes/circle'))
const WebSupport = dynamic(() => import('@/components/atoms/icons/web-support'))
const WebDesign = dynamic(() => import('@/components/atoms/icons/web-design'))
const SEO = dynamic(() => import('@/components/atoms/icons/seo'))
const Shopify = dynamic(() => import('@/components/atoms/icons/shopify'))

const HomeTemplate = (props: TemplateProps) => {
	// Props
	const { lng, setFontSize } = props

	// Variables
	const lngDir = dir(lng)
	const { t } = useTranslation(lng)
	const muiTheme = useTheme()
	const containerRef = useRef<HTMLDivElement>(null)
	const greaterThanMedium = useMediaQuery(muiTheme.breakpoints.up('md'))
	const servicesRef = useRef<HTMLDivElement>(null)

	// GSAP
	useGSAP(
		() => {
			gsap.timeline({
				scrollTrigger: {
					trigger: servicesRef.current,
					start: 'top top',
					end: '+=624',
					scrub: 1,
					// markers: true,
					onUpdate: self => {
						gsap.set(servicesRef.current, {
							xPercent: self.progress * 100 * (lngDir === 'rtl' ? 1 : -1),
							yPercent: self.progress * 100 * (greaterThanMedium ? 0.25 : 1)
						})
					}
				}
			})
		},
		{ scope: containerRef }
	)

	return (
		<Box ref={containerRef}>
			<Container maxWidth="lg">
				<Grid container justifyContent="center" position="relative" pb={{ xs: 8, md: 16 }}>
					<CircleMolecule lng={lng} position="left" />
					<CircleMolecule lng={lng} position="right" reverse />

					<Grid
						size={{ xs: 9, md: 8, lg: 6 }}
						mt={10}
						position="relative"
						onMouseEnter={() => {
							if (setFontSize) setFontSize(140)
						}}
						onMouseLeave={() => {
							if (setFontSize) setFontSize(10)
						}}
						sx={{
							cursor: 'none',
							'& > svg': {
								width: { xs: 200, sm: 381 },
								maxWidth: '80dvw',
								height: { xs: 200, sm: 381 },
								maxHeight: '80dvw',
								pointerEvents: 'none',
								position: 'absolute',
								top: '100%',
								left: '50%',
								transform: 'translate(-50%, -50%)'
							}
						}}
					>
						<Typography
							variant="h1"
							width={1}
							fontFamily={lngDir !== 'rtl' ? NexaFont.style.fontFamily : 'inherit'}
							fontSize={{ xs: 80, sm: 120, md: 155 }}
							fontWeight={700}
							lineHeight={1}
							textAlign="center"
						>
							{t('common:app.name')}
						</Typography>

						<CircleShapeAtom color="#070f2b" />

						<Box
							width="30dvw"
							height="30dvw"
							position="absolute"
							top="90%"
							sx={{
								...(lngDir === 'rtl' ? { right: '45%' } : { left: '45%' }),
								transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
							}}
						>
							<Image src="/images/items/rocket.png" alt="rocket" fill={true} style={{ objectFit: 'cover' }} />
						</Box>
					</Grid>
				</Grid>

				<Grid container justifyContent="center">
					<Grid size={{ xs: 12, md: 11, lg: 10 }} sx={{ perspective: 500 }}>
						<DialogueMolecule lng={lng} text="common:app.slogan" />
					</Grid>
				</Grid>

				<Grid component="section" container justifyContent="center" spacing={{ xs: 3, md: 6 }} position="relative" mt={{ xs: 4, md: 8 }}>
					<CircleMolecule lng={lng} position="right" reverse />

					<Grid size={12}>
						<SectionHeaderMolecule title="common:title.regiti" subtitle="common:title.services" />
					</Grid>

					<Box display="flex" width={1} height={624 * (greaterThanMedium ? 1.5 : 2)} position="relative">
						<Grid
							ref={servicesRef}
							container
							flex="0 0 fit-content"
							flexDirection="row"
							flexWrap="nowrap"
							spacing={{ xs: 2, md: 4, lg: 6 }}
							minWidth={1}
							height="fit-content"
							sx={{
								transform: 'translateX(10%)'
							}}
						>
							<CardMoelcule lng={lng} title="common:title.websiteDevelopment" description="common:description.websiteDevelopment" icon={<WebSupport />} />
							<CardMoelcule lng={lng} title="common:title.webDesign" description="common:description.webDesign" icon={<WebDesign />} reverse />
							<CardMoelcule lng={lng} title="common:title.seo" description="common:description.seo" icon={<SEO />} />
							<CardMoelcule lng={lng} title="common:title.eCommerce" description="common:description.eCommerce" icon={<Shopify />} reverse />
							<CardMoelcule lng={lng} title="common:title.mobileApp" description="common:description.mobileApp" icon={<Shopify />} />
						</Grid>
					</Box>
				</Grid>
			</Container>

			<Grid component="section" container position="relative" mt={{ xs: 4, md: 8 }}>
				<Grid size={12}>
					<AwardsMolecule lng={lng} />
				</Grid>
			</Grid>

			<Container sx={{ mt: { xs: 8, md: 16 } }}>
				<Grid component="section" container display="none" position="relative">
					<Grid size={12}>
						<SectionHeaderMolecule title="common:title.regiti" subtitle="common:title.bestWorks" />
					</Grid>
				</Grid>

				<Grid component="section" container position="relative" mt={{ xs: 4, md: 8 }}>
					<CircleMolecule lng={lng} position="center" />

					<Grid size={12}>
						<SectionHeaderMolecule title="common:title.customers" subtitle="common:title.sayAboutUs" />
					</Grid>

					<Grid size={12} zIndex={1}>
						<CustomersMolecule lng={lng} />
					</Grid>
				</Grid>
			</Container>
		</Box>
	)
}

export default HomeTemplate
